import { formatCouponUsageWindows } from "@/lib/coupon-availability"
import type { CouponTerms } from "@/lib/coupon-readiness"
import { centsToReais, formatBRL } from "@/lib/format-brl"

const REVEALED_KEY_PREFIX = "meucontroleai:coupon-revealed:"

function revealedKey(merchantId: string, code: string): string {
  return `${REVEALED_KEY_PREFIX}${merchantId}:${code.toUpperCase()}`
}

/** Whether the "Você ganhou um cupom!" sheet still has to open for this code in this visit (tab session); true when storage is unavailable. */
export function couponRevealPending(merchantId: string, code: string): boolean {
  try {
    return sessionStorage.getItem(revealedKey(merchantId, code)) !== "1"
  } catch {
    return true
  }
}

/** Records that the sheet was shown for this code, so it does not open again until the visit ends. */
export function markCouponRevealed(merchantId: string, code: string): void {
  try {
    sessionStorage.setItem(revealedKey(merchantId, code), "1")
  } catch {
    return
  }
}

const EVERY_DAY = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]

function worksAnyTime(terms: CouponTerms): boolean {
  const blocks = terms.availability?.blocks ?? []
  if (blocks.length === 0) return true
  return blocks.some(
    (block) =>
      EVERY_DAY.every((day) => block.days.includes(day)) &&
      (block.schedules.length === 0 || block.schedules.some((shift) => shift.start.startsWith("00:00") && shift.end.startsWith("23:59"))),
  )
}

/**
 * The fine print under a revealed coupon, in the customer's words: its
 * validity (already formatted for the store's clock), minimum order, weekly
 * window and first-purchase rule. Only what the public record says.
 */
export function couponRevealConditions(terms: CouponTerms | null, validity: string | null): string[] {
  const lines = validity ? [validity] : []
  if (!terms) return lines
  if (terms.minPurchaseCents > 0) lines.push(`Pedido mínimo de ${formatBRL(centsToReais(terms.minPurchaseCents))}`)
  const windows = worksAnyTime(terms) ? null : formatCouponUsageWindows(terms.availability)
  if (windows) lines.push(`Vale ${windows}`)
  if (terms.firstPurchaseOnly) lines.push("Só na primeira compra nesta loja")
  return lines
}
